import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import { AppointmentTimeFields } from './AppointmentTimeFields';
import { userManagementService } from '../../../../services/modules/userManagement/userManagement.service';
import { IUserManagementResult } from '../../../../models/user-management/UserManagementModel';
import styles from './NewAppointmentPopup.module.css';
import { AppointmentSchema } from '../../../../schemas/AppointmentSchema';
import { AppointmentFormValues } from '../../../../models/appointment/AppointmentModel';
import { toast } from 'react-toastify';

const PopupFooter = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting, submitForm } = useFormikContext<AppointmentFormValues>();
  const { uiStore } = useStore();

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isSubmitting}>
        İptal
      </CommonButton>
      <CommonButton
        type="save"
        onClick={submitForm}
        disabled={isSubmitting}
        loading={uiStore.isLoading}
      >
        Güncelle
      </CommonButton>
    </>
  );
};

// Hastane seçildiğinde kişileri yükleyen component
const PersonLoader = ({
  onHospitalChange,
}: {
  onHospitalChange: (hospitalId: number | null) => void;
}) => {
  const { values, setFieldValue } = useFormikContext<AppointmentFormValues>();
  const previousHospitalIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (values.hospitalId !== previousHospitalIdRef.current) {
      const prev = previousHospitalIdRef.current;
      previousHospitalIdRef.current = values.hospitalId;

      if (values.hospitalId) {
        onHospitalChange(values.hospitalId);
        // Sadece kullanıcı bir hastaneden diğerine geçtiyse kişiyi sıfırla (ilk açılışta bozulmasın)
        if (typeof prev === 'number') {
          setFieldValue('personId', null);
        }
      } else {
        onHospitalChange(null);
        setFieldValue('personId', null);
      }
    }
  }, [values.hospitalId, setFieldValue, onHospitalChange]);

  return null;
};

const EditAppointmentPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore, appointmentStore } = useStore();
  const [personsFromApi, setPersonsFromApi] = useState<Array<{ value: number; label: string }>>([]);
  const [users, setUsers] = useState<IUserManagementResult[]>([]);

  const selectedAppointment = appointmentStore.selectedAppointmentToEdit;

  // İlk açılışta hemen göster: randevudaki kişi + API'den gelen liste
  const personOptions = useMemo(() => {
    const apt = selectedAppointment;
    const fromApi = [...personsFromApi];
    const pid = apt?.personId != null ? Number(apt.personId) : null;
    if (pid != null && apt?.personName && !fromApi.some(o => Number(o.value) === pid)) {
      fromApi.unshift({ value: pid, label: apt.personName });
    }
    return fromApi;
  }, [personsFromApi, selectedAppointment?.personId, selectedAppointment?.personName]);

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isEditAppointmentOpen) return;

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isEditAppointmentOpen]);

  useEffect(() => {
    const loadPersons = async () => {
      const apt = appointmentStore.selectedAppointmentToEdit;
      if (!popupStore.isEditAppointmentOpen || !apt?.hospitalId) return;

      try {
        const persons = await personStore.getPersonsByHospitalId(apt.hospitalId);
        const opts = persons
          .filter(
            (p): p is { id: number; name: string } => p.id !== undefined && p.name !== undefined
          )
          .map(p => ({ value: p.id!, label: p.name! }));
        setPersonsFromApi(opts);
      } catch (error) {
        console.error('Error fetching persons:', error);
        setPersonsFromApi([]);
      }
    };
    loadPersons();
  }, [
    popupStore.isEditAppointmentOpen,
    appointmentStore.selectedAppointmentToEdit?.hospitalId,
    personStore,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!popupStore.isEditAppointmentOpen) return;
      try {
        const usersList = await userManagementService.getAllUsers();
        const actualUsers = Array.isArray(usersList)
          ? usersList
          : ((usersList as { value?: IUserManagementResult[] })?.value ?? []);
        setUsers(actualUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchUsers();
  }, [popupStore.isEditAppointmentOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const handleHospitalChange = useCallback(
    async (hospitalId: number | null) => {
      if (!hospitalId) {
        setPersonsFromApi([]);
        return;
      }

      try {
        const persons = await personStore.getPersonsByHospitalId(hospitalId);
        setPersonsFromApi(
          persons
            .filter(
              (p): p is { id: number; name: string } => p.id !== undefined && p.name !== undefined
            )
            .map(p => ({
              value: p.id!,
              label: p.name!,
            }))
        );
      } catch (error) {
        console.error('Error fetching persons:', error);
        toast.error('Kişiler yüklenirken bir hata oluştu');
        setPersonsFromApi([]);
      }
    },
    [personStore]
  );

  // İstek numarası tag'den gelsin (NewAppointment / AppointmentForHospitalTag mantığı)
  const requestNumberFromTag =
    selectedAppointment?.tagPrefix ?? selectedAppointment?.requestNumber ?? '';

  const initialValues: AppointmentFormValues = {
    hospitalId: selectedAppointment?.hospitalId || null,
    personId:
      selectedAppointment?.personId != null ? Number(selectedAppointment.personId) : null,
    tagNumberId: selectedAppointment?.tagNumberId ?? null,
    userId: selectedAppointment?.userId ?? null,
    appointmentDate: selectedAppointment?.appointmentDate
      ? new Date(selectedAppointment.appointmentDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    appointmentStartTime: selectedAppointment?.appointmentStartTime || '',
    appointmentEndTime: selectedAppointment?.appointmentEndTime || '',
    deviceList: selectedAppointment?.deviceList || '',
    requestNumber: requestNumberFromTag,
  };

  const userOptions = useMemo((): Array<{ value: string; label: string }> => {
    const opts = users
      .filter(u => u.id && !u.isDeleted)
      .map(u => ({
        value: u.id,
        label: `${u.userName}${u.firstName && u.lastName ? ` (${u.firstName} ${u.lastName})` : ''}`,
      }));

    // Mevcut randevunun kullanıcısı listede yoksa ekle (setlenmesi için)
    const apt = selectedAppointment;
    if (
      apt?.userId &&
      !opts.some(o => String(o.value) === String(apt.userId)) &&
      apt.userName
    ) {
      opts.unshift({ value: apt.userId, label: apt.userName });
    }
    return opts;
  }, [users, selectedAppointment?.userId, selectedAppointment?.userName]);

  const handleSubmit = async (values: AppointmentFormValues) => {
    try {
      if (!selectedAppointment?.id) {
        toast.error('Randevu ID bulunamadı');
        return;
      }

      if (!values.hospitalId || !values.personId) {
        toast.error('Lütfen hastane ve kişi seçiniz');
        return;
      }

      const response = await appointmentStore.updateAppointment(selectedAppointment.id, values);

      if (response.error === false) {
        popupStore.setIsEditAppointmentOpen(false);
        appointmentStore.setSelectedAppointmentToEdit(null);
      }
    } catch (error) {
      console.error('Randevu güncellenirken hata:', error);
      // Error handling is done in the store
    }
  };

  const handleClose = () => {
    popupStore.setIsEditAppointmentOpen(false);
    appointmentStore.setSelectedAppointmentToEdit(null);
    toast.dismiss();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={AppointmentSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values }) => (
        <Popup
          isOpen={popupStore.isEditAppointmentOpen}
          onClose={handleClose}
          title="Randevu Düzenle"
          footer={() => <PopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <PersonLoader onHospitalChange={handleHospitalChange} />
              <div className={styles.inputContainer}>
                <FormikDropdown
                  name="hospitalId"
                  options={hospitalOptions}
                  label="Hastane"
                  required
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="requestNumber"
                  label="Etiket Numarası"
                  placeholder="Etiket numarasını giriniz"
                  disabled
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikDropdown
                  name="personId"
                  options={personOptions}
                  label="Kişi"
                  placeholder="Önce hastane seçiniz"
                  disabled={!values.hospitalId}
                  required
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikDropdown
                  name="userId"
                  options={userOptions}
                  label="Kullanıcı"
                  placeholder="Kullanıcı seçiniz"
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="appointmentDate"
                  label="Randevu Tarihi"
                  type="date"
                  placeholder="Tarih seçiniz"
                  required
                />
              </div>

              <AppointmentTimeFields />

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="deviceList"
                  label="Cihaz Listesi"
                  placeholder="Cihaz listesini giriniz"
                  multiline
                  rows={4}
                  required
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditAppointmentPopup);
